/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'react'
import { useRegistrationSetupContext } from './context'
import GeneralSettings from './forms/GeneralSettings'
import Payments from './forms/Payments'
import Customization from './forms/Customization'
import EmailCustomization from './forms/EmailCustomization/index'
import Waiver from './forms/Waiver'
import TeamCustomForm from './forms/TeamCustomForm'
import PlayerCustomForm from './forms/PlayerCustomForm'
import UnsavedChangesModal from './forms/CustomForm/UnsavedChangesModal'
import Header from './components/Header'
import SidebarNav, { MobileSidebarNav } from './components/SidebarNav'
import { useRegistrationSetupTabs } from './hooks/useRegistrationSetupTabs'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSidebarState } from 'src/hooks/useSidebarState'
import { MENU_HEIGHT } from 'src/components/css'
import { getErrorMessage } from '@sportninja/common/utils/utils'

type RegistrationSetupContentProps = {
  setIsRegistrationFormOpen: (isOpen: boolean) => void
  refetchRegistrationFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  refetchRegistrationInfoFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  isRegistrationFormOpen: boolean
  registrationVersion: number
  isVisible: boolean
  transitionDuration: number
}

const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 15;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 88px 0px;

  @media (max-width: 768px) {
    max-width: none;
    padding: 0px;
  }
`

const bodyStyle = css`
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 16px;
  margin-top: 0;
`

const contentAreaStyle = css`
  flex: 1;
  overflow-y: auto;
`

const RegistrationSetupContent = ({
  setIsRegistrationFormOpen,
  refetchRegistrationFn,
  isRegistrationFormOpen,
  registrationVersion,
  isVisible,
  transitionDuration,
}: RegistrationSetupContentProps) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284

  const {
    submitRegistration,
    state,
    registration,
    isUpdated,
    refetchWaivers,
    apiWaivers,
    activeTab,
    setActiveTab,
  } = useRegistrationSetupContext()

  const { message } = App.useApp()

  const isLegacyRegistration = registrationVersion === 1
  const tabs = useRegistrationSetupTabs(isLegacyRegistration)

  // Refs for custom forms to check unsaved changes
  const teamFormRef = useRef<{ hasPendingChanges: () => boolean }>(null)
  const playerFormRef = useRef<{ hasPendingChanges: () => boolean }>(null)

  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const contentAreaRef = useRef<HTMLDivElement>(null)
  const [hasScroll, setHasScroll] = useState(false)

  useLayoutEffect(() => {
    const el = contentAreaRef.current
    if (!el) {
      return
    }

    const checkScroll = () => {
      setHasScroll(el.scrollHeight > el.clientHeight)
    }

    checkScroll()

    const resizeObserver = new ResizeObserver(checkScroll)
    resizeObserver.observe(el)
    // Observe the first child so we pick up content growth/shrink
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild)
    }

    return () => resizeObserver.disconnect()
  }, [activeTab])

  useEffect(() => {
    if (isRegistrationFormOpen) {
      refetchWaivers()
    }
  }, [isRegistrationFormOpen, refetchWaivers])

  // Redirect from disabled waiver tab
  useEffect(() => {
    if (activeTab === '2' && (!apiWaivers || apiWaivers.length === 0)) {
      setActiveTab('0')
    }
  }, [activeTab, apiWaivers])

  // Redirect from disabled email tab
  useEffect(() => {
    const bothDisabled =
      !state.enableTeamRegistration && !state.enablePlayerRegistration
    if (activeTab === '6' && bothDisabled) {
      setActiveTab('0')
    }
  }, [
    activeTab,
    state.enableTeamRegistration,
    state.enablePlayerRegistration,
    setActiveTab,
  ])

  const getFormById = (id: string) => {
    switch (id) {
      case '0':
        return <GeneralSettings />
      case '1':
        return <Payments />
      case '2':
        return <Waiver />
      case '3':
        return <TeamCustomForm ref={teamFormRef} />
      case '4':
        return <PlayerCustomForm ref={playerFormRef} />

      case '5':
        return <Customization />
      case '6':
        return <EmailCustomization />
      default:
        return null
    }
  }

  const executeSubmit = useCallback(async () => {
    try {
      setSubmitError(null)
      await submitRegistration()
      message.success(
        isUpdated
          ? 'Registration Updated Successfully'
          : 'Registration Created Successfully'
      )
      await refetchRegistrationFn?.current?.(isUpdated ? false : true)
      setIsRegistrationFormOpen(false)
    } catch (error: any) {
      if (error?.invalid_fields) {
        if (error.invalid_fields.currency_country_id) {
          setActiveTab('1')
        }
        if (error.invalid_fields.payment_type) {
          setActiveTab('1')
        }
        return
      }
      const errorMessage = getErrorMessage(error)
      setSubmitError(errorMessage)
    }
  }, [
    submitRegistration,
    message,
    isUpdated,
    refetchRegistrationFn,
    setIsRegistrationFormOpen,
    setActiveTab,
  ])

  const handleMainSave = useCallback(() => {
    const teamUnsaved = teamFormRef.current?.hasPendingChanges?.()
    const playerUnsaved = playerFormRef.current?.hasPendingChanges?.()
    const unsaved = teamUnsaved || playerUnsaved

    if (unsaved) {
      setShowUnsavedModal(true)
      return
    }

    executeSubmit()
  }, [executeSubmit])

  const handleClose = useCallback(() => {
    setIsRegistrationFormOpen(false)
  }, [setIsRegistrationFormOpen])

  const NavComponent = isMobile ? MobileSidebarNav : SidebarNav

  return (
    <div
      css={css`
        ${overlayStyle}
        top: ${isMobile ? 48 : MENU_HEIGHT}px;
        left: ${sidebarWidth}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
        transition: opacity ${transitionDuration}ms ease-out,
          transform ${transitionDuration}ms ease-out, left 0.2s ease-in-out;
      `}
    >
      <div css={contentWrapperStyle}>
        <Header
          onBack={handleClose}
          onCancel={handleClose}
          onSubmit={handleMainSave}
          isLoading={state.loading}
          submitError={submitError}
          competitionName={
            registration?.root_schedule?.name_full ||
            registration?.root_schedule?.name ||
            undefined
          }
        />

        <div
          css={css`
            ${bodyStyle}
            flex-direction: ${isMobile ? 'column' : 'row'};
          `}
        >
          <NavComponent
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <div
            ref={contentAreaRef}
            css={css`
              ${contentAreaStyle}
              padding-right: ${hasScroll ? '8px' : '0'};
            `}
          >
            {getFormById(activeTab)}
          </div>
        </div>

        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          isLoading={state.loading}
          error={null}
          onClose={() => setShowUnsavedModal(false)}
          onConfirm={async () => {
            setShowUnsavedModal(false)
            await executeSubmit()
          }}
        />
      </div>
    </div>
  )
}

export default RegistrationSetupContent
